import { Box, Typography, useTheme, Paper } from "@mui/material";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../authentication/AuthProvider";
import ResetButton from "../components/button/ResetButton";
import ResizableTextBox from "../components/input/ResizableTextBox";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { EnvFactor } from "../db_access/env_factor";
import { LifeCycleAssessment as LCA } from "../db_access/life_cycle_assessment";
import { Process } from "../db_access/process";
import { Product } from "../db_access/product";
import { Project } from "../db_access/project";
import { ReusedProduct } from "../db_access/reused_product";

import { getOrgTokens, ORG_PALETTE } from "../theme/style/organizationPalette";
import "../style/components-style/LifeCycleAssessment.css";
const LifeCycleAssessment = forwardRef(({ selectedRow, archive, isArivuLibrary, isAdmin, LCA_type = Product.label }, parentRef) => {
  const { t } = useTranslation();
  const { isSaveClicked, setRefresh, setIsSaveVisible, envFactorOptions, setIsSaveClicked, setIsLoading, refresh } = useContextProvider();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const [processes, setProcessData] = useState([]);
  const [envFactors, setEnvFactors] = useState([]);
  const [editableLcaInfo, setEditableLcaInfo] = useState({});
  const readOnly = !isAdmin && isArivuLibrary;
  const [isModified, setIsModified] = useState(false);
  const emptyLCA = useMemo(
    () => ({
      product_id: null,
      project_id: null,
      reused_product_id: null,
      reference_pcr: null,
      declared_unit: null,
      scope: null,
      lca_software: null,
      lci_database: null,
      lcia_methodology: null,
      cutoff: null,
      allocation_procedure: null,
      primary_sources: null,
      interpretation: null,
    }),
    []
  );
  const inputs = [
    { label: "Primary Sources", field: "primary_sources", minHeight: 80, span: 12 },
    { label: "Interpretation", field: "interpretation", minHeight: 80, span: 12 },
  ];
  useImperativeHandle(parentRef, () => ({
    getEditableLCAData: () => editableLcaInfo,
  }));
  const findReference = (type, category, sub_category, description, envFactorOptions) => {
    const temp = envFactorOptions?.[type]?.[category]?.[sub_category || ""]?.[description] || [];
    if (temp.length > 0) {
      return temp[0].reference || "N/A";
    }
    return "N/A";
  };

  const newLCA = useCallback(
    (id, type) => {
      const lca = {
        ...emptyLCA,
        product_id: type === Product.label ? id : null,
        project_id: type === Project.label ? id : null,
        reused_product_id: type === ReusedProduct.label ? id : null,
      };
      if (!lca.product_id && !lca.project_id && !lca.reused_product_id) return;
      LCA.new(lca);
    },
    [emptyLCA]
  );
  const getLcaInfo = useCallback(
    (id, type) => {
      if (!id) {
        setEditableLcaInfo(emptyLCA);
        return;
      }
      let request = null;
      if (type === Product.label) {
        request = LCA.get_by_product_id;
      } else if (type === Project.label) {
        request = LCA.get_by_project_id;
      } else if (type === ReusedProduct.label) {
        request = LCA.get_by_reused_product_id;
      }
      if (request) {
        request(id)
          .then((res) => res.json())
          .then((data) => {
            if (data.length === 0) {
              newLCA(id, type);
            } else {
              setEditableLcaInfo(data[0]);
            }
          });
      }
    },
    [emptyLCA, newLCA]
  );

  const getProcesses = useCallback((id, type) => {
    if (!id) {
      setProcessData([]);
      return;
    }
    let request = null;
    if (type === Product.label) {
      request = Process.get_by_product_ids;
    } else if (type === Project.label) {
      request = Process.get_by_project_ids;
    }
    if (request) {
      request([id])
        .then((res) => res.json())
        .then((data) => setProcessData(data));
    }
  }, []);

  const getEnvFactors = (process_ids) => {
    if (!process_ids || process_ids.length === 0) {
      setEnvFactors([]);
      return;
    }
    EnvFactor.get_by_process_ids(process_ids)
      .then((res) => res.json())
      .then((data) => setEnvFactors(data));
  };

  const updateLcaInfo = useCallback(async () => {
    if (LCA_type === Product.label) {
      LCA.update_lca_by_product(editableLcaInfo).finally(() => {
        setIsModified(false);
        setIsSaveClicked(false);
        setIsLoading(false);
        setRefresh((r) => !r);
      });
    } else if (LCA_type === Project.label) {
      LCA.update_lca_by_project(editableLcaInfo).finally(() => {
        setIsModified(false);
        setIsSaveClicked(false);
        setIsLoading(false);
        setRefresh((r) => !r);
      });
    } else if (LCA_type === ReusedProduct.label) {
      LCA.update_lca_by_reused_product(editableLcaInfo).finally(() => {
        setIsModified(false);
        setIsSaveClicked(false);
        setIsLoading(false);
        setRefresh((r) => !r);
      });
    }
  }, [LCA_type, editableLcaInfo, setIsModified, setIsSaveClicked, setIsLoading, setRefresh]);

  const handleChange = (field, value) => {
    if (readOnly) return;
    setEditableLcaInfo((prev) => ({ ...prev, [field]: value }));
    setIsModified(true);
  };
  const reset = () => {
    if (selectedRow && selectedRow.id) {
      LCA.reset_for_reused_product(selectedRow.id).finally(() => {
        setIsModified(false);
        setIsSaveVisible(false);
        setRefresh((r) => !r);
      });
    }
  };

  useEffect(() => {
    // Smart Refresh: If the user is currently editing (isModified), 
    // skip the auto-refresh to avoid overwriting their work.
    if (isModified) return;

    getLcaInfo(selectedRow?.id, LCA_type);
    getProcesses(selectedRow?.id, LCA_type);
  }, [selectedRow?.id, LCA_type, getLcaInfo, getProcesses, refresh, isModified]);

  useEffect(() => {
    getEnvFactors(processes.map((p) => p.id));
  }, [processes]);

  useEffect(() => {
    const formattedEnvFactors = envFactors
      .map((factor) => {
        const process = processes.find((p) => p.id === factor.process_id) || {};
        const reference = findReference(factor.type, factor.category, factor.sub_category, factor.description, envFactorOptions);
        return `• LCA Stage: ${process.lca_stage || "N/A"}, Process Name: ${process.name || "N/A"}, Type: ${factor.type}, Category: ${factor.category}, Sub-Category: ${
          factor.sub_category
        }, Description: ${factor.description}, Reference: ${reference}`;
      })
      .filter(Boolean)
      .join("\n");

    setEditableLcaInfo((prevInfo) => ({
      ...prevInfo,
      formattedReferences: formattedEnvFactors,
    }));
  }, [envFactors, processes, envFactorOptions]);

  useEffect(() => {
    if (isModified) {
      setIsSaveVisible(true);
    }
  }, [isModified, setIsSaveVisible]);

  useEffect(() => {
    if (isModified && isSaveClicked) {
      updateLcaInfo();
      setIsModified(false);
    }
  }, [isSaveClicked, updateLcaInfo, isModified]);

  return (
    <Paper variant="Table Container">
      <TableHeaderContainer>
        <TableTitleContainer>
          <Typography variant="Table Title">{t("Life Cycle Interpretation")}</Typography>
        </TableTitleContainer>
      </TableHeaderContainer>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" }, gap: 3 }}>
          {inputs.map((input, index) => {
            return (
              <Box key={index} sx={{ gridColumn: { xs: "span 12", md: `span ${input.span || 12}` } }}>
                <ResizableTextBox
                  label={input.label}
                  value={editableLcaInfo?.[input.field] || ""}
                  onChange={(e) => handleChange(input.field, e.target.value)}
                  disabled={readOnly || archive}
                  placeholder={input.label}
                  minHeight={input.minHeight}
                />
              </Box>
            );
          })}
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 12" } }}>
            <ResizableTextBox
              label={"References / Emission Factors"}
              value={editableLcaInfo?.formattedReferences}
              disabled
              placeholder={"References / Emission Factors"}
              minHeight={80}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
});

export default LifeCycleAssessment;
